import { fft, nextPow2 } from './fft.ts';

/**
 * Envelope of a real signal via the analytic (Hilbert) signal.
 *
 * Zero the negative frequencies, double the positive ones, transform back,
 * take the magnitude. This is what makes an echo read as one smooth bump
 * instead of a burst of oscillation whose peak sample lands wherever the
 * carrier phase happened to fall.
 */
export function hilbertEnvelope(x: ArrayLike<number>): Float64Array {
  const n = x.length;
  if (n === 0) return new Float64Array(0);
  const size = nextPow2(n);
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  for (let i = 0; i < n; i++) re[i] = x[i];

  fft(re, im, false);

  const half = size >> 1;
  for (let k = 1; k < half; k++) {
    re[k] *= 2;
    im[k] *= 2;
  }
  for (let k = half + 1; k < size; k++) {
    re[k] = 0;
    im[k] = 0;
  }
  // Bins 0 and size/2 are left alone: they are real and shared by both halves.

  fft(re, im, true);

  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = Math.hypot(re[i], im[i]);
  return out;
}

/**
 * Band-limit and envelope in one transform, with a raised-cosine taper.
 *
 * The obvious construction - Butterworth band-pass, then Hilbert - is what
 * the 1-D range profile uses, and for reading a single trace it is fine. It
 * is not fine for a map. A recursive filter's impulse response rings, and the
 * thing it is ringing on here is fifty decibels louder than anything being
 * looked for: the direct blast is the sound travelling 5 cm from the speaker
 * to the microphone on the same handset, while a wall two metres off comes
 * back forty-odd decibels down. Sidelobes at -40 dB of an enormous impulse
 * are the same size as the echoes, and they land at fixed ranges in every
 * look of the scan, so they are not noise that averages away - they are a
 * consistent false room, and being consistent they fit a plane better than
 * the real one does. Measured on a simulated room containing no surfaces
 * whatsoever, that ringing produced arrivals standing 9 to 12 dB clear of
 * their own background.
 *
 * Doing it in the frequency domain removes the mechanism rather than
 * suppressing the symptom. Zeroing the negative frequencies is already what
 * the analytic signal needs, so restricting the positive ones to the analysis
 * band costs one multiply per bin and no extra transform. The taper is what
 * matters: a hard spectral edge is a rectangle in frequency and therefore a
 * sinc in time, whose sidelobes decay only as 1/t, while a raised-cosine edge
 * decays as 1/t^3. That is the whole difference between a phantom at -40 dB
 * and one at -75 dB.
 */
export function bandpassEnvelope(
  x: ArrayLike<number>,
  sampleRate: number,
  lowHz: number,
  highHz: number,
  taperHz = 0,
): Float64Array {
  const n = x.length;
  if (n === 0) return new Float64Array(0);
  const size = nextPow2(n);
  const re = new Float64Array(size);
  const im = new Float64Array(size);
  for (let i = 0; i < n; i++) re[i] = x[i];

  fft(re, im, false);

  // Taper width scaled to the band by default. A fixed narrow taper is nearly
  // a rectangle when the band is wide, and a rectangle in frequency is a sinc
  // in time whose sidelobes fall off as 1/t - which is exactly the phantom
  // this function exists to remove. Roughly a sixth of the bandwidth puts the
  // first sidelobe far below any real echo while costing negligible resolution.
  const top0 = Math.min(highHz, sampleRate * 0.5);
  const taper = taperHz > 0 ? taperHz : Math.max(400, 0.16 * (top0 - lowHz));
  const binHz = sampleRate / size;
  const half = size >> 1;
  const top = Math.min(highHz, sampleRate * 0.5);

  for (let k = 0; k <= half; k++) {
    const hz = k * binHz;
    let w: number;
    if (hz <= lowHz - taper || hz >= top + taper) {
      w = 0;
    } else if (hz < lowHz) {
      w = 0.5 - 0.5 * Math.cos((Math.PI * (hz - (lowHz - taper))) / taper);
    } else if (hz > top) {
      w = 0.5 + 0.5 * Math.cos((Math.PI * (hz - top)) / taper);
    } else {
      w = 1;
    }
    // Positive frequencies doubled, as the analytic signal requires; bins 0
    // and Nyquist are real and shared, so they are not.
    const gain = k === 0 || k === half ? w : 2 * w;
    re[k] *= gain;
    im[k] *= gain;
  }
  for (let k = half + 1; k < size; k++) {
    re[k] = 0;
    im[k] = 0;
  }

  fft(re, im, true);

  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = Math.hypot(re[i], im[i]);
  return out;
}
